import {Card, closeCurrentDialog, DataStatus, openDialog, Parameter} from 'platform/components';
import {Show} from 'platform/foundation';

import {useGetVehicleQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';

import {buildArray, composePath, Nullish, suffixTestId, TestIdProps, useToggle} from 'shared/web';

import {AftersaleVehicleOverview} from '../../AftersalesVehicleWidgetCard/components/AftersaleVehicleOverview';
import {ServiceVehicleForm} from '../../ServiceVehicleForm/ServiceVehicleForm';
import {ServiceVehicleFormMode} from '../../ServiceVehicleForm/types/ServiceVehicleFormMode';
import {ServiceVehicleFormVariant} from '../../ServiceVehicleForm/types/ServiceVehicleFormVariant';

interface SuggestedVehicleProps extends TestIdProps {
  vehicleId: string;
  vehicleTitle: string | Nullish;
  vehicleRegistrationPlate: string | Nullish;
  vehicleVin: string | Nullish;
  isSelected: boolean;
  customerTypeTitle: string | Nullish;
  onSelect: (vehicleId: string | null) => void;
  isExpanded?: boolean;
}

export function SuggestedVehicle(props: SuggestedVehicleProps) {
  const [isExpanded, expand] = useToggle(props.isExpanded);

  const {
    data: vehicle,
    isLoading,
    isError,
  } = useGetVehicleQuery({vehicleId: props.vehicleId}, {skip: !isExpanded});

  const onVehicleEdit = () => {
    if (!props.vehicleId) {
      return;
    }

    openDialog(
      <ServiceVehicleForm
        mode={ServiceVehicleFormMode.EDIT}
        variant={ServiceVehicleFormVariant.SIMPLE}
        vehicleId={props.vehicleId}
        onSuccess={closeCurrentDialog}
        onDiscard={closeCurrentDialog}
        data-testid={suffixTestId('edit', props)}
      />,
      {title: i18n.t('entity.vehicle.labels.editVehicle'), withAdditionalFooter: true}
    );
  };

  return (
    <Card
      variant="inlineWhite"
      title={props.vehicleTitle}
      flags={
        props.customerTypeTitle
          ? [
              {
                label: props.customerTypeTitle,
                colorScheme: 'blue',
                size: 'small',
                isSubtle: true,
              },
            ]
          : undefined
      }
      parameters={buildArray<Parameter>()
        .when(props.vehicleRegistrationPlate, props.vehicleRegistrationPlate)
        .when(props.vehicleVin, props.vehicleVin)}
      control={{
        type: 'radio',
        value: props.isSelected,
        onChange: () => props.onSelect(props.vehicleId || null),
      }}
      isExpandable
      onExpandButtonClick={expand}
      isExpanded={isExpanded}
      actions={[
        {
          type: 'iconButton',
          size: 'small',
          icon: 'action/open_in_new',
          onClick: () => {
            if (!props.vehicleId) {
              return;
            }

            window.open(composePath(vehiclesRoutes.detail, {params: {id: vehicle?.id}}), '_blank');
          },
        },
        {
          type: 'iconButton',
          size: 'small',
          icon: 'image/edit',
          onClick: onVehicleEdit,
        },
      ]}
    >
      <DataStatus isLoading={isLoading} isError={isError} minHeight={12}>
        <Show when={vehicle}>
          {(vehicle) => (
            <AftersaleVehicleOverview
              vehicle={vehicle}
              data-testid={suffixTestId('overview', props)}
            />
          )}
        </Show>
      </DataStatus>
    </Card>
  );
}
